import { useImmer } from "use-immer";
import { useRef, useState, useEffect, useContext, useMemo } from "react";
import SimpleReactValidator from "simple-react-validator";
import { useDebounce } from "use-debounce";
import { usePermission, useStatusSelect } from "../../../shared";
import { AppContext } from "../../../store";
import { useFinishedProductsListQueries } from "./useFinishedProductsListQueries";
import { useCategoryQueries } from "../../MasterData/hooks/useCategoryQueries";

export const useFinishedProductsList = () => {
  const moduleName = "finishedProductsList";
  const {
    tableData,
    setSorting,
    setPagination,
    setSearchState,
    setTableState,
    clearAllFilters,
  } = useContext(AppContext);

  // const { search } = tableData[moduleName];
  const [state, setState] = useImmer({
    isLoading: true,
    dataList: { categoryfilter: [] },
    filters: {},
    isModalOpen: false,
    isDefaultId: null,
  });

  const { listQuery: categoryListQuery } = useCategoryQueries({
    categorylist: true,
  });

  const CategoryOptions = categoryListQuery?.data?.map((v) => {
    return { id: v.id, name: v.name };
  });
  const { sorting, pagination, search } = tableData[moduleName];
  const [debouncedText] = useDebounce(search, 1000);

  const { listQuery, updatePOSSync, changeDefaultStatus } =
    useFinishedProductsListQueries({
      selectedCategory: tableData?.finishedProductsList?.data?.categoryfilter,
      selectedType: state.selectedType,
      selectedStatus: tableData?.finishedProductsList?.data?.selectedStatus,
      selectedSubCategory:
        tableData?.finishedProductsList?.data?.subcategoryfilter,
      debouncedText,
      sorting,
      pagination,
    });

  useEffect(() => {
    // getUnits();
    if (listQuery?.data?.recipes) {
      setState((draft) => {
        draft.dataList = transformData(listQuery?.data.recipes || []);
        draft.isLoading = false;
      });
    }
  }, [listQuery?.data]);

  const transformData = (apiData) => {
    return Object.entries(apiData).flatMap(([plu, value]) =>
      value.recipes.map((r, index, arr) => ({
        ...r,
        plu,
        firstPlu: index === 0,
        lastPlu: index === arr.length - 1, // ✅ mark last item
      })),
    );
  };

  const categoryValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({
      tableName: moduleName,
      key: "categoryfilter",
      value: item,
    });
  };

  const onChangeStatusHandler = (value) => {
    setTableState({
      tableName: moduleName,
      key: "selectedStatus",
      value: value,
    });
  };

  const selectStatus = useStatusSelect({ onChangeStatusHandler });

  const onClearFilter = () => {
    clearAllFilters({ tableName: moduleName });
  };

  // const selectedType = tableData?.finishedProductsWithoutPlu?.data?.selectedType;

  const selectedCategoryIds = useMemo(() => {
    return (
      tableData?.finishedProductsList?.data?.categoryfilter?.map((c) => c.id) ||
      []
    );
  }, [tableData?.finishedProductsList?.data?.categoryfilter]);

  const SubCategoryOptions = useMemo(() => {
    if (!selectedCategoryIds.length || !categoryListQuery?.data) return [];

    return categoryListQuery.data
      .filter((cat) => selectedCategoryIds.includes(cat.id))
      .flatMap((cat) => cat.subData || [])
      .map((sub) => ({
        id: sub.id,
        name: sub.name,
      }));
  }, [selectedCategoryIds, categoryListQuery?.data]);

  const subCategoryValuesHandler = (event, option) => {
    const item = option?.map((item) => {
      return { ...item };
    });
    setTableState({
      tableName: moduleName,
      key: "subcategoryfilter",
      value: item,
    });
  };

  useEffect(() => {
    setTableState({
      tableName: moduleName,
      key: "subcategoryfilter",
      value: [],
    });
  }, [selectedCategoryIds]);

  const onSearchHandler = (search) => {
    setSearchState({
      tableName: moduleName,
      key: "search",
      value: search,
    });
    setPagination({
      tableName: moduleName,
      callback: (prevPagination) => ({
        ...prevPagination,
        pageIndex: 1,
      }),
    });
  };

  const categoryFilter = tableData?.finishedProductsList?.data?.categoryfilter;
  const selectedStatus = tableData?.finishedProductsList?.data?.selectedStatus;

  const disableclearButton = !(
    (Array.isArray(categoryFilter) && categoryFilter.length > 0) ||
    (selectedStatus !== null && selectedStatus !== "")
  );

  const handleUpdatePOSSync = (type, id) => {
    let recipeIds = [];

    if (type === "single") {
      recipeIds = [id];
    } else {
      recipeIds = state.dataList
        .filter((item) => item.set_as_default === 1)
        .map((item) => item.id);
    }

    updatePOSSync.mutate({
      recipe_ids: recipeIds,
    });
  };

  const handleSetDefaultStatus = (row, data) => {
    if (!data) {
      setState((draft) => {
        draft.isDefaultId = null;
        draft.isModalOpen = false;
      });
    } else {
      setState((draft) => {
        draft.isModalOpen = true;
        draft.isDefault = row;
      });
    }
  };

  const handleChangeDefaultStatus = (id) => {
    changeDefaultStatus.mutate({ id, handleSetDefaultStatus });
  };

  return {
    state,
    listQuery,
    moduleName,
    categoryValuesHandler,
    CategoryOptions,
    tableData,
    selectStatus,
    onChangeStatusHandler,
    onClearFilter,
    disableclearButton,
    SubCategoryOptions,
    subCategoryValuesHandler,
    SubCategoryOptions,
    subCategoryValuesHandler,
    search,
    pagination,
    sorting,
    setPagination,
    setSorting,
    onSearchHandler,
    handleUpdatePOSSync,
    handleChangeDefaultStatus,
    updatePOSSync,
    handleSetDefaultStatus,
  };
};
